// Considered: PlaceholderFactorSourceSection (a single line) and RollupRosterMolecule (the paginated, filterable
// editing surface, which must not render on a read-first pane). See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { RollupExplanationMemberRow, RollupExplanationResponse } from 'types/spherePoints';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import ShareBarAtom, { type ShareBarSegment } from '../../atoms/ShareBar/ShareBarAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import RosterMemberIdentity from '../RollupRoster/cells/RosterMemberIdentity';
import { buildReadsAsSentence, type ReadsAsSentenceParams } from '../RollupSetCard/readsAsSentence';
import type { RollupSetModel } from '../RollupSetCard/rollupSetModel';
import { buildWhySentence } from './buildWhySentence';
import type { UseRollupExplanationResult } from './useRollupExplanation';
import './RollupSummarySection.scss';

export interface RollupSummarySectionProps {
  explanation: UseRollupExplanationResult;
  /** The RESULT row (and any schedule warning) the summary owns; rendered inside the why-card. */
  resultBlock: React.ReactNode;
  /** The factor's own status word, for the error message when the route gives none. */
  statusLabel: string;
  onOpenConfigure?: () => void;
  /** The factor's stored set; its READS AS sentence closes the why-sentence. */
  rollupSet?: RollupSetModel;
  /** Lookups READS AS needs to name the set's conditions. */
  readsAsContext?: Pick<ReadsAsSentenceParams, 'availableProperties' | 'availableRelationships' | 'availablePerspectives'>;
}

/** Share of the track each kind takes: the route's shares cover the reporting pool, the not-yet-measured run is its share of the whole set. */
function toBarSegments(data: RollupExplanationResponse): ShareBarSegment[] {
  const segments = data.segments ?? [];
  const unmeasured = segments.find((s) => s.kind === 'unmeasured');
  const total = data.explanation?.counts.total ?? 0;
  const unmeasuredShare = unmeasured && total > 0 ? Math.min(1, unmeasured.count / total) : 0;
  const poolScale = 1 - unmeasuredShare;
  const bar: ShareBarSegment[] = [];
  for (const segment of segments) {
    if (segment.kind === 'member') bar.push({ id: `member:${segment.sphereId}`, kind: 'status', status: segment.status, share: segment.share * poolScale });
    else if (segment.kind === 'run') bar.push({ id: `run:${segment.status}`, kind: 'status', status: segment.status, share: segment.share * poolScale });
    else if (segment.kind === 'hidden') bar.push({ id: 'hidden', kind: 'hidden', share: segment.share * poolScale });
  }
  if (unmeasuredShare > 0) bar.push({ id: 'unmeasured', kind: 'unmeasured', share: unmeasuredShare });
  return bar;
}

const RowStatus: React.FC<{ row: RollupExplanationMemberRow }> = ({ row }) => {
  const { t } = useTranslation();
  if (row.kind === 'hidden') {
    return <StatusBadgeAtom variant="hidden" label={t('statusFactors.roster.hiddenStatus', 'status hidden')} />;
  }
  if (row.kind === 'unmeasured') {
    return <StatusBadgeAtom variant="not-yet-measured" label={t('statusFactors.roster.notYetMeasured', 'not yet measured')} />;
  }
  if (row.kind === 'broken') {
    return (
      <StatusBadgeAtom
        variant="broken"
        label={t(`statusFactors.roster.brokenReason.${row.contributionUnavailable.reason}`, row.contributionUnavailable.reason)}
      />
    );
  }
  return <StatusBadgeAtom variant="measured" statusValue={row.status} label={t(`statusFactors.roster.statusValue.${row.status}`, row.status)} />;
};

const RollupSummarySection: React.FC<RollupSummarySectionProps> = ({
  explanation,
  resultBlock,
  statusLabel,
  onOpenConfigure,
  rollupSet,
  readsAsContext,
}) => {
  const { t, i18n } = useTranslation();
  const { data, loading, error, stalled, reload } = explanation;
  const showSkeleton = !error && !stalled && (loading || !data || data.pending);

  const whyCard = (body: React.ReactNode) => (
    <section className="rollup-summary-section__why" data-testid="rollup-summary-why">
      <span className="rollup-summary-section__why-label">{t('statusFactors.summary.rollup.whyLabel')}</span>
      {resultBlock}
      {body}
    </section>
  );

  if (error) {
    return (
      <div className="rollup-summary-section" data-testid="rollup-summary-section" data-state="error">
        {whyCard(
          <div className="rollup-summary-section__error" role="alert" data-testid="rollup-summary-error">
            <p>{t('statusFactors.summary.rollup.errorMessage', { status: statusLabel })}</p>
            <ButtonAtom type="button" variant="text" label={t('statusFactors.summary.rollup.retry')} onClick={reload} data-testid="rollup-summary-retry" />
          </div>
        )}
      </div>
    );
  }

  if (stalled) {
    return (
      <div className="rollup-summary-section" data-testid="rollup-summary-section" data-state="stalled">
        {whyCard(
          <div className="rollup-summary-section__error" data-testid="rollup-summary-stalled">
            <output className="rollup-summary-section__message">{t('statusFactors.summary.rollup.stalled')}</output>
            <ButtonAtom type="button" variant="text" label={t('statusFactors.summary.rollup.checkAgain')} onClick={reload} data-testid="rollup-summary-check-again" />
          </div>
        )}
      </div>
    );
  }

  if (showSkeleton || !data?.explanation) {
    return (
      <div className="rollup-summary-section" data-testid="rollup-summary-section" data-state="loading">
        {whyCard(
          <div className="rollup-summary-section__skeleton" data-testid="rollup-summary-skeleton" aria-busy="true">
            <SkeletonAtom count={2} height="var(--font-size-md)" />
            <SkeletonAtom height="var(--spacing-sm)" />
            {data?.pending && (
              <output className="rollup-summary-section__caption" data-testid="rollup-summary-pending">
                {t('statusFactors.summary.rollup.pending')}
              </output>
            )}
          </div>
        )}
        <div className="rollup-summary-section__members">
          <SkeletonAtom count={3} height="var(--spacing-xl)" />
        </div>
      </div>
    );
  }

  const { explanation: facts, members = [], moreCount = 0, unmeasuredNames = [] } = data;
  const readThrough = facts.readThrough
    ? (readsAsContext?.availablePerspectives?.find((p) => p.systemName === facts.readThrough)?.label ?? facts.readThrough)
    : facts.readThrough;
  const readsAs = rollupSet
    ? buildReadsAsSentence({
        set: rollupSet,
        t,
        locale: i18n?.language,
        availableRelationships: readsAsContext?.availableRelationships,
        availablePerspectives: readsAsContext?.availablePerspectives,
      })
    : null;
  const sentence = buildWhySentence({ ...facts, readThrough }, t, readsAs);
  const hasHidden = facts.counts.hidden > 0;
  const isEmptyPopulation = facts.branch === 'empty_population';
  const barLabel = t('statusFactors.summary.rollup.barLabel', {
    measured: facts.counts.measured,
    hidden: facts.counts.hidden,
    unmeasured: facts.counts.unmeasured,
  });

  return (
    <div className="rollup-summary-section" data-testid="rollup-summary-section" data-state="ready" data-branch={facts.branch}>
      {whyCard(
        <>
          {sentence && (
            <p className="rollup-summary-section__sentence" data-testid="rollup-summary-sentence">
              {sentence}
            </p>
          )}
          {!isEmptyPopulation && (
            <>
              <ShareBarAtom segments={toBarSegments(data)} label={barLabel} />
              <p className="rollup-summary-section__caption" data-testid="rollup-summary-caption">
                {t('statusFactors.summary.rollup.barCaption')}
                {hasHidden && t('statusFactors.summary.rollup.barCaptionHidden')}
              </p>
            </>
          )}
        </>
      )}

      <section className="rollup-summary-section__members" aria-labelledby="rollup-summary-members-title">
        <header className="rollup-summary-section__members-header">
          <h4 id="rollup-summary-members-title" className="rollup-summary-section__members-title">
            {t('statusFactors.summary.rollup.inThisSet')} <span className="rollup-summary-section__members-count">({facts.counts.total})</span>
          </h4>
          <ButtonAtom
            type="button"
            variant="text"
            className="rollup-summary-section__edit-rule"
            label={t('statusFactors.summary.rollup.editRule')}
            onClick={() => onOpenConfigure?.()}
            data-testid="rollup-summary-edit-rule"
          />
        </header>
        {isEmptyPopulation ? (
          <p className="rollup-summary-section__empty" data-testid="rollup-summary-empty-members">
            {t('statusFactors.summary.rollup.emptyMembers')}
          </p>
        ) : (
          <table className="rollup-summary-section__table">
            <thead>
              <tr>
                <th scope="col">
                  <span>{t('statusFactors.summary.rollup.columnSphere')}</span>
                </th>
                <th scope="col">
                  <span>{t('statusFactors.summary.rollup.columnStatus')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((row, index) => (
                <tr
                  key={row.kind === 'hidden' ? `hidden-${index}` : row.sphereId}
                  className={`rollup-summary-section__row rollup-summary-section__row--${row.kind}`}
                  data-testid="rollup-summary-row"
                  data-kind={row.kind}
                  data-status={row.kind === 'measured' ? row.status.replaceAll('_', '-') : undefined}
                >
                  <td>
                    <RosterMemberIdentity
                      hidden={row.kind === 'hidden'}
                      name={row.kind === 'hidden' ? null : row.name}
                      glyph={row.kind === 'hidden' ? null : row.glyph}
                    />
                  </td>
                  <td>
                    <RowStatus row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {moreCount > 0 && (
          <ButtonAtom
            type="button"
            variant="text"
            className="rollup-summary-section__more"
            label={t('statusFactors.summary.rollup.moreEnrolled', { n: moreCount })}
            onClick={() => onOpenConfigure?.()}
            data-testid="rollup-summary-more"
          />
        )}
        {unmeasuredNames.length > 0 && (
          <p className="rollup-summary-section__footnote" data-testid="rollup-summary-footnote">
            <span className="rollup-summary-section__footnote-glyph" aria-hidden="true" />
            <span>{t('statusFactors.summary.rollup.footnote', { names: unmeasuredNames.join(', ') })}</span>
          </p>
        )}
      </section>
    </div>
  );
};

export default RollupSummarySection;
